import { Component, Inject, LOCALE_ID, ViewEncapsulation } from '@angular/core';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { parse, format as formatDate } from 'date-fns';
import {
  DateWidgetComponent,
  AdfDateFnsAdapter,
  ADF_DATE_FORMATS,
} from '@alfresco/adf-core';
import {
  enUS,
  fr,
  de,
  it,
  es,
  ja,
  zhCN,
  nl,
  pt,
  nb,
  ru,
  ar,
  cs,
  pl,
  fi,
  da,
  sv,
} from 'date-fns/locale';
import { DateFnsAdapter } from '@angular/material-date-fns-adapter';

const event = 'event($event)';
@Component({
  selector: 'wk-date-widget',
  templateUrl: `./wk-date-widget.component.html`,
  styleUrls: ['./wk-date-widget.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    { provide: MAT_DATE_FORMATS, useValue: ADF_DATE_FORMATS },
    { provide: DateAdapter, useClass: AdfDateFnsAdapter },
  ],
  host: {
    '(click)': event,
    '(blur)': event,
    '(change)': event,
    '(focus)': event,
    '(focusin)': event,
    '(focusout)': event,
    '(input)': event,
    '(invalid)': event,
    '(select)': event,
  },
})
export class WkDateWidgetComponent extends DateWidgetComponent {
  protected dateAdapterRef: DateAdapter<Date>;
  private readonly today = new Date();
  public dateFormatFixed = 'dd-MM-yyyy';
  public defaultDateFormat = 'MM-dd-yyyy';
  public errorMessagesHandler: string;
  public localErrors: boolean;

  constructor(
    dateAdapter: DateAdapter<Date, DateFnsAdapter>,
    @Inject(LOCALE_ID) private readonly locale: string
  ) {
    super();
    this.dateAdapterRef = dateAdapter;
  }

  /**
   * Maps Angular locale codes to date-fns locale objects for AdfDateFnsAdapter.
   */
  private getDateFnsLocale(localeCode: string): any {
    const localeMap: { [key: string]: any } = {
      en: enUS,
      fr,
      de,
      it,
      es,
      ja,
      zh: zhCN,
      nl,
      pt,
      nb,
      ru,
      ar,
      cs,
      pl,
      fi,
      da,
      sv,
    };

    const baseLocale = localeCode.split('-')[0].toLowerCase();
    return localeMap[baseLocale] || enUS;
  }

  ngOnInit(): void {
    super.ngOnInit();
    // Set the correct date-fns locale for AdfDateFnsAdapter
    const requestedLocale = this.locale || 'en';
    const dateFnsLocale = this.getDateFnsLocale(requestedLocale);

    try {
      this.dateAdapterRef.setLocale(dateFnsLocale);
    } catch (error) {
      // Fallback to English if locale setting fails
      this.dateAdapterRef.setLocale(enUS);
    }

    // Configure custom date display format if specified
    if (this.field?.dateDisplayFormat) {
      this.dateFormatFixed = this.field.dateDisplayFormat.includes('D')
        ? this.field.dateDisplayFormat
            .replace('D', 'dd')
            .replace('M', 'MM')
            .replace('YYYY', 'yyyy')
        : this.field.dateDisplayFormat;
      const adapter = this.dateAdapterRef as AdfDateFnsAdapter;
      adapter.displayFormat = this.dateFormatFixed;
    }
    if (this.field.readOnly && this.field.value) {
      this.field.value = formatDate(this.field.value, this.dateFormatFixed);
    }
    // Set min/max date constraints for the date picker
    if (this.field) {
      if (this.field.minValue) {
        this.minDate = parse(
          this.field.minValue,
          this.defaultDateFormat,
          this.today
        );
      } else {
        this.minDate = this.today;
      }

      if (this.field.maxValue) {
        this.maxDate = parse(
          this.field.maxValue,
          this.defaultDateFormat,
          this.today
        );
      }
    }
  }

  public onDateChanged(newDateValue: any): void {
    const date: Date = parse(
      newDateValue.value,
      this.dateFormatFixed,
      this.today
    );
    if (!isNaN(date.getTime())) {
      this.field.value = formatDate(date, this.dateFormatFixed);
      const fieldsReady =
        this.field.form.values['priority'] &&
        this.field.form.values['assignee'];
      if (!this.field.form.isValid && this.field.value && fieldsReady) {
        this.errorMessagesHandler =
          'ADF_PROCESS_LIST.START_PROCESS.FORM.ERRORS.PAST_DUE_DATE';
        this.dateInputControl.setErrors({ matDatepickerMin: true });
        this.localErrors = true;
      } else {
        this.errorMessagesHandler = '';
        this.dateInputControl.setErrors(null);
        this.localErrors = false;
      }
    } else {
      this.errorMessagesHandler = this.field.dateDisplayFormat;
      this.dateInputControl.setErrors({ matDatepickerParse: true });
      this.localErrors = true;
      this.field.value = '';
      newDateValue.value = '';
    }
  }
}
